import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ACTIVE_THEME, getThemeVars } from '../themes/dashboardThemes';
import api from '../services/api';
import { buildLoginPath, getSafeRedirectPath } from '../utils/redirect';

function VerifyEmailNew() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const theme = getThemeVars(ACTIVE_THEME);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [resent, setResent] = useState(false);
  const [resending, setResending] = useState(false);

  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const token = searchParams.get('token');
  const afterLoginPath = getSafeRedirectPath(searchParams.get('after_login_url'), '/');
  const loginPath = buildLoginPath(afterLoginPath);

  useEffect(() => {
    let isMounted = true;

    const verify = async () => {
      if (!token) {
        if (isMounted) {
          setError(t('Token de verificación no encontrado'));
          setLoading(false);
        }
        return;
      }

      try {
        await api.get(`/auth/verify/${encodeURIComponent(token)}`);
        if (isMounted) {
          setSuccess(true);
          setLoading(false);
        }
      } catch (err) {
        if (isMounted) {
          const status = err?.response?.status;
          if (status === 400 || status === 404) {
            setError(t('Token de verificación inválido o expirado'));
          } else {
            setError(err?.response?.data?.message || t('Error al verificar el email'));
          }
          setLoading(false);
        }
      }
    };

    verify();
    return () => { isMounted = false; };
  }, [t, token]);

  useEffect(() => {
    if (!success) return undefined;
    const timeoutId = setTimeout(() => navigate(loginPath), 2000);
    return () => clearTimeout(timeoutId);
  }, [success, navigate, loginPath]);

  const handleResend = async () => {
    if (!token) return;
    setResending(true);
    setError('');

    try {
      await api.post('/auth/resend-verification-from-token', { token });
      setResent(true);
    } catch (err) {
      setError(err?.response?.data?.message || t('Error al reenviar el email'));
    } finally {
      setResending(false);
    }
  };

  return (
    <div
      className="dashboard-new font-body min-h-[100dvh] flex items-center justify-center px-4"
      style={{ ...theme, backgroundColor: 'var(--chrome-bg)', color: 'var(--chrome-text)' }}
    >
      <div className="w-full max-w-md">
        <div className="bg-surface rounded-xl shadow-sm border border-outline p-8">
          <h1 className="text-xl font-bold text-center text-on-surface">{t('Verificación de correo')}</h1>
          <p className="text-sm text-center text-on-surface-variant mt-1">{t('Confirma tu email para continuar usando tu cuenta')}</p>

          <div className="h-px bg-outline-variant/20 my-5" />

          {loading && (
            <div className="flex flex-col items-center py-6">
              <div className="animate-spin rounded-full h-10 w-10 border-2 border-primary border-t-transparent" />
              <p className="text-on-surface-variant mt-3">{t('Verificando tu correo...')}</p>
            </div>
          )}

          {!loading && success && (
            <div className="flex flex-col items-center gap-4 py-4">
              <div className="w-[72px] h-[72px] rounded-full bg-primary-container flex items-center justify-center">
                <span className="material-symbols-outlined text-primary text-[42px]">check_circle</span>
              </div>
              <h2 className="text-lg font-bold text-on-surface text-center">{t('¡Email verificado exitosamente!')}</h2>
              <p className="text-sm text-on-surface-variant text-center">{t('Serás redirigido al login...')}</p>
              <button
                onClick={() => navigate(loginPath)}
                className="flex items-center gap-2 px-6 py-2.5 rounded-lg bg-primary text-on-primary font-medium transition-opacity hover:opacity-90"
              >
                <span className="material-symbols-outlined text-[20px]">login</span>
                {t('Ir a Login')}
              </button>
            </div>
          )}

          {!loading && error && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 p-3 rounded-lg bg-error-container text-on-error-container text-sm">
                <span className="material-symbols-outlined text-[18px]">error</span>
                {error}
              </div>
              {token && (
                <button
                  onClick={handleResend}
                  disabled={resending}
                  className="w-full py-2.5 rounded-lg bg-primary text-on-primary font-medium transition-opacity hover:opacity-90 disabled:opacity-50"
                >
                  {t('Reenviar email de verificación')}
                </button>
              )}
              <button
                onClick={() => navigate('/register')}
                className="w-full py-2.5 rounded-lg border-2 border-outline-variant/30 text-on-surface font-medium hover:bg-surface-container-low transition-colors"
              >
                {t('Volver a Registro')}
              </button>
            </div>
          )}

          {!loading && resent && (
            <div className="space-y-3 mt-4">
              <div className="flex items-center gap-2 p-3 rounded-lg bg-primary-container text-on-primary-container text-sm">
                <span className="material-symbols-outlined text-[18px]">check_circle</span>
                {t('Email de verificación reenviado. Revisa tu bandeja de entrada.')}
              </div>
              <button
                onClick={() => navigate(loginPath)}
                className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg bg-primary text-on-primary font-medium transition-opacity hover:opacity-90"
              >
                <span className="material-symbols-outlined text-[20px]">mark_email_read</span>
                {t('Ir a Login')}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default VerifyEmailNew;
